import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IItemForGetShortInfoWithStatus, EntityStatus } from 'types';
import { Rule } from 'antd/lib/form';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import FormSelect from 'components/Select/FormSelect';
import { ISelectOption } from 'components/Select/types';
import { DropdownRender } from 'components/Select/DropdownRender';
import { ShowArchived } from 'components/Select/DropdownRender/ShowArchived';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';

interface IProps {
	setIDRestaurant: (value: Nullable<string>) => void;
	fetchedRestaurants: IItemForGetShortInfoWithStatus[];
	selectedRestaurantId: Nullable<string>;
}

export const RestaurantSelect: React.FC<IProps> = React.memo(
	({ setIDRestaurant, fetchedRestaurants, selectedRestaurantId }) => {
		const { t } = useTranslation('error');
		const validationRules = useMemo<Rule[]>(() => [{ required: true, message: t('selectRestaurant') }], [t]);
		const [showArchived, setShowArchived] = useState<boolean>(
			localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
		);

		const mappedOptions = useMemo<ISelectOption[]>(() => {
			const visibleItems = showArchived
				? fetchedRestaurants
				: fetchedRestaurants.filter(
						(restaurant) =>
							restaurant.status === EntityStatus.Active || restaurant.id === selectedRestaurantId,
				  );

			return visibleItems.map((restaurant) => ({
				...restaurant,
				additionalContent: restaurant.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
			}));
		}, [fetchedRestaurants, showArchived, selectedRestaurantId]);

		const toggleShowArchived = useCallback(() => {
			localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
			setShowArchived((prevValue) => !prevValue);
		}, [showArchived]);

		const renderSelectMenu = useCallback(
			(menu: React.ReactElement) => (
				<DropdownRender menu={menu}>
					<ShowArchived checked={showArchived} onChange={toggleShowArchived} />
				</DropdownRender>
			),
			[showArchived, toggleShowArchived],
		);

		return (
			<FormSelect
				testId="RestaurantSelect"
				name="IDSelectedRestaurant"
				label="restaurantLabel"
				options={mappedOptions}
				onChange={setIDRestaurant}
				validationRules={validationRules}
				keepSearchWithDropdownRender={true}
				dropdownRender={renderSelectMenu}
			/>
		);
	},
);
